import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import {
	BootstrapSettings,
	IntegrationsStatus,
	NavBreadcrumb,
	PicTimeDataResponse,
	ServerDataResponse
} from '@models/models';
import { Subject } from 'rxjs/Subject';
import { IntegrationsDataProvider } from '@services/integrations/integrations.data.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { ShootProofDataProvider } from '@services/integrations/shootProof/shootProof.data.service';
import { SettingsProvider } from '@services/settings/settings';
import { SNModalService } from '@web-services/modal/snModal.service';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { IntegrateQuickBooksModal } from '@web-components/settings/integrations-section/modals/integrate-quick-books-modal/integrate-quick-books-modal';
import { IntegrateShootProofModal } from '@web-components/settings/integrations-section/modals/integrate-shoot-proof-modal/integrate-shoot-proof-modal';
import { IntegrateWithXeroV2Modal } from '@web-components/settings/integrations-section/modals/integrate-with-xero-v2/integrate-with-xero-v2-modal';
import { IntegrateXeroModal } from '@web-components/settings/integrations-section/modals/integrate-xero-modal/integrate-xero-modal';
import { finalize, takeUntil } from 'rxjs/operators';
import { PickTimeDataProvider } from '@services/integrations/pickTime/pickTime.data.service';
import { IntegratePicTimeModal } from '@web-components/settings/integrations-section/modals/integrate-pic-time-modal/integrate-pic-time-modal';
import { ConfirmGeneralModalComponent } from '@web-components/common/confirm-general-modal/confirm-general-modal.component';

@Component({
	selector: 'integrations-section',
	template: require('./integrations-section.html'),
	styles: [require('./integrations-section.scss')]
})
export class IntegrationsSection implements OnInit, OnDestroy {
	@Input() routeParams: any;

	breadcrumbs: Array<NavBreadcrumb> = [
		{ link: '/', title: 'Dashboard' },
		{ link: '/settings', title: 'Settings' },
		{ link: null, title: 'Integrations' }
	];
	accountsQB = {};
	accountsList = {};

	xeroOrganization: string;
	quickbooksOrganization: string;
	shootProofCompanyName: string;
	lockIntegrationButtons: boolean;
	isXeroSpinningWheelActive: boolean;
	isXeroDisconnectSpinningWheelActive: boolean;
	isQbSpinningWheelActive: boolean;
	isQbDisconnectSpinningWheelActive: boolean;
	isShootProofSpinningWheelActive: boolean;
	isSpDisconnectSpinningWheelActive: boolean;
	shootProofCode = null;
	shootProofIntroduction: string;
	isFundySpinningWheelActive: boolean;
	connectedToFundy: boolean;
	isFundyDisconnectSpinningWheelActive: boolean;
	isProSelectSpinningWheelActive: boolean;
	connectedToProSelect: boolean;
	isProSelectDisconnectSpinningWheelActive: boolean;
	fundyAvailable: boolean;
	proSelectAvailable: boolean;
	integrateXeroModalRef: NgbModalRef;
	integrateWithXeroV2Modal: NgbModalRef;
	integrateShootProofModalRef: NgbModalRef;
	integrateQuickBooksModalRef: NgbModalRef;
	preIntegratePickTimeModalRef: NgbModalRef;
	integratePickTimeModalRef: NgbModalRef;
	connectedToPicTime: boolean;
	qbIntegration = null;
	xeroIntegration = null;
	shootProofIntegration = null;
	picTimeIntegration = null;
	picTimeCompanyName: string;
	xeroV2Connections = null;
	activeToken: string;
	isPicTimeSpinningWheelActive: boolean;
	isPicTimeDisconnectSpinningWheelActive: boolean;
	clientPortalText: string;
	picTimeAccessToken: string;
	picTimeAvailable: boolean;

	private unsubscribe: Subject<void> = new Subject();

	constructor(
		private integrationsDataService: IntegrationsDataProvider,
		private messageService: MessageService,
		private shootProofDataService: ShootProofDataProvider,
		private settingsService: SettingsProvider,
		private modalService: SNModalService,
		private pickTimeDataProvider: PickTimeDataProvider,
		public location: Location
	) {}
	ngOnInit(): void {
		this.getIntegrationStatus();
		this.initSettingsIntegration();
		const token = this.routeParams.xero;
		const tokenPicTime = this.routeParams.accessToken;
		if (token) {
			if (this.activeToken !== token) {
				this.activeToken = token;
				this.handleReceivedToken(token);
			}
		} else if (tokenPicTime) {
			this.handlePicTimeToken(tokenPicTime);
		} else {
			this.activeToken = '';
		}
		this.trackXeroV2Redirect();
		if (window.location.search.includes('?cancel=yes')) {
			window.history.replaceState(
				null,
				null,
				window.location.pathname.replace(
					window.location.pathname,
					'/settings/integrations'
				)
			);
		}
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	getIntegrationStatus(): void {
		this.lockIntegrationButtons = true;
		this.integrationsDataService
			.getIntegrationsV1()
			.pipe(
				takeUntil(this.unsubscribe),
				finalize((): boolean => (this.lockIntegrationButtons = false))
			)
			.subscribe(
				(data: IntegrationsStatus) => this.successGetIntegrationStatus(data),
				error => this.errorGetIntegrationStatus(error)
			);
	}

	initSettingsIntegration(): void {
		this.settingsService.timeFormatSettings
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((data: BootstrapSettings) => {
				if (data.fundyAvailable) {
					this.fundyAvailable = true;
					if (data.fundyIntegration === 'ENABLED') {
						this.connectedToFundy = true;
					}
				}

				if (data.proSelectAvailable) {
					this.proSelectAvailable = true;
					if (data.proSelectIntegration === 'ENABLED') {
						this.connectedToProSelect = true;
					}
				}

				if (data.picTimeAvailable) {
					this.picTimeAvailable = true;
				}
			});
	}

	successGetIntegrationStatus(data: IntegrationsStatus): void {
		const {
			xeroIntegration,
			qbIntegration,
			shootProofIntegration,
			picTimeIntegration
		} = data;
		this.qbIntegration = qbIntegration;
		this.xeroIntegration = xeroIntegration;
		this.shootProofIntegration = shootProofIntegration;
		this.picTimeIntegration = picTimeIntegration;

		if (xeroIntegration.connectedOrganisation) {
			this.xeroOrganization =
				xeroIntegration.connectedOrganisation.connectedOrganisation;
		} else if (qbIntegration.connectedOrganisation) {
			this.quickbooksOrganization =
				qbIntegration.connectedOrganisation.connectedOrganisation;
		} else {
			console.log('NO_INTEGRATION');
		}
		if (shootProofIntegration.connectedOrganisation) {
			this.shootProofCompanyName =
				shootProofIntegration.connectedOrganisation.connectedOrganisation;
		}
		if (picTimeIntegration.actions.CONNECT_PIC_TIME) {
			this.connectedToPicTime = false;
		} else {
			this.connectedToPicTime = true;
		}
		this.picTimeCompanyName = picTimeIntegration.accountName;
	}

	errorGetIntegrationStatus({ error }): void {
		const { errorTitle, errorMessage } = error;
		this.messageService.showErrorFromBackend(errorTitle, errorMessage);
	}

	handleReceivedToken(token): void {
		const params = this.routeParams;
		this.lockIntegrationButtons = true;
		if (token === 'xero') {
			this.messageService.showToaster('i_0', 'xero');
			this.handleXeroToken(params);
		} else if (token === 'qb') {
			this.messageService.showToaster('i_0', 'qb');
			this.handleQuickbooksToken(params);
		} else if (token === 'shootproof') {
			this.messageService.showToaster('i_0', 'shootProof');
			this.handleShootproofToken(params);
		} else {
			console.error('Unknown token');
		}
	}

	handleXeroToken(params): void {
		this.isXeroSpinningWheelActive = true;

		if (this.accountsList) {
			delete this.accountsList;
		}

		this.saveXeroIntegration(params);
	}

	saveXeroIntegration(params): void {
		this.integrationsDataService
			.sendXeroToken(params)
			.pipe(
				finalize(() => {
					this.isXeroSpinningWheelActive = false;
					this.lockIntegrationButtons = false;
				}),
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				data => this.showChooseAccountModal(data),
				error => this.messageService.showDefaultErrorMessage(error)
			);
	}

	initXeroIntegration(): void {
		if (
			this.confirmModalRequired(this.xeroIntegration.actions.CONNECT_V0) ||
			this.confirmModalRequired(this.xeroIntegration.actions.CONNECT)
		) {
			return;
		}

		this.lockIntegrationButtons = true;
		this.isXeroSpinningWheelActive = true;

		if (this.xeroIntegration.actions.CONNECT_V0) {
			this.integrationsDataService
				.initXeroIntegration()
				.pipe(
					finalize(() => {
						this.isXeroSpinningWheelActive = false;
						this.lockIntegrationButtons = false;
					}),
					takeUntil(this.unsubscribe)
				)
				.subscribe(
					data => {
						this.doRedirect(data.value);
					},
					result => this.errorConnectToXero(result.data)
				);
		}
		if (this.xeroIntegration.actions.CONNECT) {
			this.doRedirect(this.xeroIntegration.actions.CONNECT.url);
		}
	}

	editXeroIntegrationSettings(): void {
		this.lockIntegrationButtons = true;
		this.isXeroSpinningWheelActive = true;
		const { connectedOrganisation } = this.xeroIntegration;

		if (
			connectedOrganisation.actions &&
			connectedOrganisation.actions.EDIT_V0
		) {
			this.integrationsDataService
				.editXeroSettings()
				.pipe(
					finalize(() => {
						this.isXeroSpinningWheelActive = false;
						this.lockIntegrationButtons = false;
					}),
					takeUntil(this.unsubscribe)
				)
				.subscribe(
					data => this.showChooseAccountModal(data),
					error => this.editXeroSettingsErrorCallback(error)
				);
		}
		if (connectedOrganisation.actions && connectedOrganisation.actions.EDIT) {
			this.getXeroV2ModalData(connectedOrganisation.actions.EDIT.url);
		}
	}

	showChooseAccountModal(accountsList): void {
		this.accountsList = Object.assign({}, accountsList);
		this.integrateXeroModalRef = this.modalService.openModal(
			IntegrateXeroModal,
			{
				windowClass: 'ng-bootstrap-modal-middle'
			}
		);
		this.integrateXeroModalRef.componentInstance.accountList = this.accountsList;
	}

	confirmModalRequired(xeroAction): boolean {
		if (
			xeroAction &&
			xeroAction.method === 'ALERT_MODAL' &&
			xeroAction.alertMessage.errorCode === 801
		) {
			const { title, message } = xeroAction.alertMessage;
			this.openXeroAlertModal(title, message);
			return true;
		} else if (
			xeroAction &&
			xeroAction.method === 'ALERT_MODAL' &&
			xeroAction.alertMessage.errorCode === 102
		) {
			const { title, message } = xeroAction.alertMessage;
			this.openXeroAlertModalWithoutRedirection(title, message);
			return true;
		}
		return false;
	}

	editXeroSettingsErrorCallback({ data, status }): void {
		const errorMessage = data ? data.errorMessage : null;

		let errorCode, errorPlace;

		if (errorMessage && status >= 400 && status < 500) {
			if (
				errorMessage.includes(
					'Xero access token expired, reconnect Xero please'
				) ||
				errorMessage.includes(
					'Bummer! Xero access token not authorized or revoked, reconnect Xero please'
				) ||
				errorMessage.includes(
					'Bummer! Xero access token revoked, reconnect Xero please'
				)
			) {
				errorCode = 'e_14';
				errorPlace = 'xero';
			} else if (
				errorMessage.includes("You don't have an active Xero subscription")
			) {
				errorCode = 'e_15';
				errorPlace = 'xero';
			}
		} else {
			errorCode = 'e_0';
			errorPlace = 'general';
		}

		this.messageService.showToaster(errorCode, errorPlace);
	}

	disconnectXero(): void {
		this.lockIntegrationButtons = true;
		this.isXeroDisconnectSpinningWheelActive = true;

		const { connectedOrganisation } = this.xeroIntegration;

		if (
			connectedOrganisation.actions &&
			connectedOrganisation.actions.DISCONNECT_V0
		) {
			this.integrationsDataService
				.disconnectXero()
				.pipe(
					finalize(() => {
						this.lockIntegrationButtons = false;
						this.isXeroDisconnectSpinningWheelActive = false;
					}),
					takeUntil(this.unsubscribe)
				)
				.subscribe(
					() => this.successDisconnectXero(),
					error => this.messageService.showDefaultErrorMessage(error)
				);
		}
		if (
			connectedOrganisation.actions &&
			connectedOrganisation.actions.DISCONNECT
		) {
			this.integrationsDataService
				.disconnectXeroV2(connectedOrganisation.actions.DISCONNECT.url)
				.pipe(
					finalize(() => {
						this.lockIntegrationButtons = false;
						this.isXeroDisconnectSpinningWheelActive = false;
					}),
					takeUntil(this.unsubscribe)
				)
				.subscribe(
					() => this.successDisconnectXero(),
					err => this.errorGetIntegrationStatus(err)
				);
		}
	}

	trackXeroV2Redirect(): void {
		if (
			window.location.pathname.includes('/integrations/xero/connect/complete')
		) {
			this.messageService.showToaster('i_0', 'xero');
			this.getXeroV2ModalData();
		}
	}

	getXeroV2ModalData(url?: string): void {
		this.lockIntegrationButtons = true;
		this.isXeroSpinningWheelActive = true;

		this.integrationsDataService
			.getXeroAuthorizedConnections(url)
			.pipe(
				finalize(() => {
					this.lockIntegrationButtons = false;
					this.isXeroSpinningWheelActive = false;
				}),
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				data => {
					this.xeroV2Connections = data;
					this.integrateWithXeroV2Modal = this.modalService.openModal(
						IntegrateWithXeroV2Modal,
						{
							windowClass: 'ng-bootstrap-modal-middle'
						}
					);
					this.integrateWithXeroV2Modal.componentInstance.connectionsData = this.xeroV2Connections;
					this.integrateWithXeroV2Modal.result.then((state: boolean): void => {
						this.onSaveXeroConnection(state);
					});
				},
				err => this.errorGetIntegrationStatus(err)
			);
	}

	onSaveXeroConnection(state): void {
		if (state) {
			this.messageService.showToaster('s_0', 'xero');
		}
	}

	// QUICKBOOKS
	handleQuickbooksToken(params): void {
		this.isQbSpinningWheelActive = true;
		if (this.accountsQB) {
			delete this.accountsQB;
		}
		this.saveQuickbooksIntegration(params);
	}

	saveQuickbooksIntegration(params): void {
		this.integrationsDataService
			.sendQuickbooksToken(params)
			.pipe(
				finalize(() => {
					this.isQbSpinningWheelActive = false;
					this.lockIntegrationButtons = false;
				}),
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				data => this.showQuickBooksModal(data),
				err => this.errorSend(err)
			);
	}

	errorSend(err): void {
		const data = err.data || null;
		if (!data) {
			return;
		}
		if (data.errorMessage.includes('companies are not supported yet')) {
			this.messageService.showToaster('e_8', 'qb');
		} else if (
			data.errorMessage ===
			'Sorry, your QuickBooks company should have at least one Sales account'
		) {
			this.messageService.showToaster('e_2', 'qb');
		} else if (
			data.errorMessage ===
			'Sorry, your QuickBooks company should have at least one Tax Code with sales tax rates'
		) {
			this.messageService.showToaster('e_21', 'qb');
		}
	}

	initQuickbooksIntegration(): void {
		this.lockIntegrationButtons = true;
		this.isQbSpinningWheelActive = true;
		if (
			this.qbIntegration &&
			this.qbIntegration.actions.CONNECT_V0.method === 'ALERT_MODAL' &&
			this.qbIntegration.actions.CONNECT_V0.alertMessage.errorCode === 102
		) {
			const {
				title,
				message
			} = this.qbIntegration.actions.CONNECT_V0.alertMessage;
			this.openXeroAlertModalWithoutRedirection(title, message);
			this.lockIntegrationButtons = false;
			this.isQbSpinningWheelActive = false;
			return;
		}
		if (this.qbIntegration.actions.CONNECT_V0) {
			this.integrationsDataService
				.initQuickbooksIntegration()
				.pipe(
					finalize(() => {
						this.isQbSpinningWheelActive = false;
						this.lockIntegrationButtons = false;
					}),
					takeUntil(this.unsubscribe)
				)
				.subscribe(
					data => this.doRedirect(data.value),
					error => this.errorConnectToToQuickbooks(error)
				);
		}
	}

	errorConnectToToQuickbooks({ data, status }): void {
		const errorMessage = data.errorMessage;
		let errorCode, errorPlace, value;
		if (errorMessage && status >= 400 && status < 500) {
			if (
				errorMessage === 'You already have integration with accounting service'
			) {
				const title = 'Slow down, Quickbooks error!';
				const text =
					'Your account is already connected with another accounting platform. Please disconnect and try again.';
				this.openXeroAlertModalWithoutRedirection(title, text);
				return;
			}
			switch (errorMessage) {
				case 'Sorry, your QuickBooks company should have at least one Sales account':
					errorCode = 'e_2';
					errorPlace = 'qb';
					break;
				default:
					errorCode = 'e_2';
					errorPlace = 'general';
					value = errorMessage;
			}
		} else {
			errorCode = 'e_0';
			errorPlace = 'general';
		}
		this.messageService.showToaster(errorCode, errorPlace, value);
	}

	editQuickBooksIntegrationSettings(): void {
		this.lockIntegrationButtons = true;
		this.isQbSpinningWheelActive = true;

		this.integrationsDataService
			.editQuickBooksSettings()
			.pipe(
				finalize(() => {
					this.isQbSpinningWheelActive = false;
					this.lockIntegrationButtons = false;
				}),
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				data => this.showQuickBooksModal(data),
				error => this.messageService.showDefaultErrorMessage(error)
			);
	}

	showQuickBooksModal(accountsQB: object): void {
		this.accountsQB = Object.assign({}, accountsQB);
		this.integrateQuickBooksModalRef = this.modalService.openModal(
			IntegrateQuickBooksModal,
			{
				windowClass: 'ng-bootstrap-modal-middle'
			}
		);
		this.integrateQuickBooksModalRef.componentInstance.accountList = this.accountsQB;
	}

	disconnectQuickbooks(): void {
		this.lockIntegrationButtons = true;
		this.isQbDisconnectSpinningWheelActive = true;

		this.integrationsDataService
			.disconnectQuickbooks()
			.pipe(
				finalize(() => {
					this.lockIntegrationButtons = false;
					this.isQbDisconnectSpinningWheelActive = false;
				}),
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				() => this.successDisconnectQuickbooks(),
				error => this.messageService.showDefaultErrorMessage(error)
			);
	}

	// ShootProof
	initShootProofIntegration(): void {
		this.lockIntegrationButtons = true;
		this.isShootProofSpinningWheelActive = true;

		this.shootProofDataService
			.initShootProofIntegrationV1()
			.pipe(
				finalize(() => {
					this.isShootProofSpinningWheelActive = false;
					this.lockIntegrationButtons = false;
				}),
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				data => this.doRedirect(data.data.url),
				error => this.messageService.showDefaultErrorMessage(error)
			);
	}

	handleShootproofToken(params): void {
		if (!params.error) {
			this.saveShootProofIntegration(params);
		} else {
			window.history.replaceState(
				null,
				null,
				window.location.pathname.replace('/shootproof', '')
			);
		}
	}

	saveShootProofIntegration(params): void {
		this.shootProofDataService
			.getExistingShootProofSettingsV1()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				result => {
					this.shootProofIntroduction =
						result.data !== null ? result.data.clientPortalText : '';
					this.shootProofCode = params.code;
					this.integrateShootProofModalRef = this.modalService.openModal(
						IntegrateShootProofModal,
						{
							windowClass: 'ng-bootstrap-modal-middle'
						}
					);
					this.integrateShootProofModalRef.componentInstance.introductionText = this.shootProofIntroduction;
					this.integrateShootProofModalRef.componentInstance.shootProofCode = this.shootProofCode;
				},
				error => this.messageService.showDefaultErrorMessage(error)
			);
	}

	editShootProofIntegrationSettings(): void {
		this.lockIntegrationButtons = true;
		this.isShootProofSpinningWheelActive = true;
		this.shootProofDataService
			.getExistingShootProofSettingsV1()
			.pipe(
				finalize(() => {
					this.isShootProofSpinningWheelActive = false;
					this.lockIntegrationButtons = false;
				}),
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				result => {
					this.shootProofCode = null;
					this.shootProofIntroduction = result.data.clientPortalText || '';
					this.integrateShootProofModalRef = this.modalService.openModal(
						IntegrateShootProofModal,
						{
							windowClass: 'ng-bootstrap-modal-middle'
						}
					);
					this.integrateShootProofModalRef.componentInstance.introductionText = this.shootProofIntroduction;
				},
				error => this.messageService.showDefaultErrorMessage(error)
			);
	}

	disconnectShootProof(): void {
		this.lockIntegrationButtons = true;
		this.isSpDisconnectSpinningWheelActive = true;

		this.shootProofDataService
			.disconnectShootProofV1()
			.pipe(
				finalize(() => {
					this.lockIntegrationButtons = false;
					this.isSpDisconnectSpinningWheelActive = false;
				}),
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				() => this.successDisconnectShootProof(),
				error => this.messageService.showDefaultErrorMessage(error)
			);
	}

	// Fundy
	connectToFundy(): void {
		this.lockIntegrationButtons = true;
		this.isFundySpinningWheelActive = true;

		this.integrationsDataService
			.updateFundyIntegrationState(true)
			.pipe(
				finalize(() => {
					this.lockIntegrationButtons = false;
					this.isFundySpinningWheelActive = false;
				}),
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				() => {
					this.messageService.showToaster('s_0', 'fundy');
					this.connectedToFundy = true;
				},
				error => {
					this.messageService.showDefaultErrorMessage(error);
				}
			);
	}

	disconnectFundy(): void {
		this.lockIntegrationButtons = true;
		this.isFundyDisconnectSpinningWheelActive = true;

		this.integrationsDataService
			.updateFundyIntegrationState(false)
			.pipe(
				finalize(() => {
					this.lockIntegrationButtons = false;
					this.isFundyDisconnectSpinningWheelActive = false;
				}),
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				() => {
					this.messageService.showToaster('s_1', 'fundy');
					this.connectedToFundy = false;
				},
				error => {
					this.messageService.showDefaultErrorMessage(error);
				}
			);
	}

	// ProSelect
	connectToProSelect(): void {
		this.lockIntegrationButtons = true;
		this.isProSelectSpinningWheelActive = true;

		this.integrationsDataService
			.updateProSelectIntegrationState(true)
			.pipe(
				finalize(() => {
					this.lockIntegrationButtons = false;
					this.isProSelectSpinningWheelActive = false;
				}),
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				() => {
					this.messageService.showToaster('s_0', 'proSelect');
					this.connectedToProSelect = true;
				},
				error => {
					this.messageService.handleError(error);
				}
			);
	}

	disconnectProSelect(): void {
		this.lockIntegrationButtons = true;
		this.isProSelectDisconnectSpinningWheelActive = true;

		this.integrationsDataService
			.updateProSelectIntegrationState(false)
			.pipe(
				finalize(() => {
					this.lockIntegrationButtons = false;
					this.isProSelectDisconnectSpinningWheelActive = false;
				}),
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				() => {
					this.messageService.showToaster('s_1', 'proSelect');
					this.connectedToProSelect = false;
				},
				error => {
					this.messageService.handleError(error);
				}
			);
	}

	// Helpers
	successDisconnectShootProof(): void {
		this.shootProofCompanyName = null;
		this.messageService.showToaster('s_10', 'shootProof');
		this.getIntegrationStatus();
	}

	successDisconnectQuickbooks(): void {
		this.quickbooksOrganization = null;
		this.messageService.showToaster('s_1', 'qb');
		this.getIntegrationStatus();
	}

	successDisconnectXero(): void {
		this.xeroOrganization = null;
		this.xeroIntegration = null;
		this.messageService.showToaster('s_2', 'xero');
		this.getIntegrationStatus();
	}

	successDisconnectPicTime(): void {
		this.connectedToPicTime = false;
		this.picTimeIntegration = null;
		this.messageService.showToaster('s_1', 'picTime');
		this.getIntegrationStatus();
	}

	doRedirect(url): void {
		this.lockIntegrationButtons = true;
		window.location.assign(url);
	}

	errorConnectToXero(data) {
		if (
			data.errorMessage ===
			'You already have integration with accounting service'
		) {
			this.messageService.showToaster('e_13', 'xero');
		}
	}

	// Pic-Time
	connectToPicTime(): void {
		this.lockIntegrationButtons = true;
		this.isPicTimeSpinningWheelActive = true;
		this.pickTimeDataProvider
			.initPickTimeIntegration()
			.pipe(
				finalize(() => {
					this.lockIntegrationButtons = true;
					this.isPicTimeSpinningWheelActive = true;
				}),
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				(data: ServerDataResponse<PicTimeDataResponse>): void => {
					this.doRedirect(data.data.integrationUrl);
				},
				error => {
					this.messageService.handleError(error);
				}
			);
	}

	handlePicTimeToken(tokenPickTime: string): void {
		this.isPicTimeSpinningWheelActive = true;

		if (!this.routeParams.error) {
			this.savePicTimeIntegration(tokenPickTime);
		} else {
			window.history.replaceState(
				null,
				null,
				window.location.pathname.replace(
					window.location.pathname,
					'/settings/integrations'
				)
			);
		}
	}

	savePicTimeIntegration(tokenPickTime: string): void {
		this.pickTimeDataProvider
			.getPreFillPicTimeSettings()
			.pipe(
				finalize(() => {
					this.isPicTimeSpinningWheelActive = false;
				}),
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				(data): void => {
					this.clientPortalText =
						data.data.clientPortalText !== null
							? data.data.clientPortalText
							: '';
					this.picTimeAccessToken = tokenPickTime;
					this.integratePickTimeModalRef = this.modalService.openModal(
						IntegratePicTimeModal,
						{
							windowClass: 'ng-bootstrap-modal-middle'
						}
					);
					this.integratePickTimeModalRef.componentInstance.clientPortalText = this.clientPortalText;
					this.integratePickTimeModalRef.componentInstance.picTimeAccessToken = this.picTimeAccessToken;
					this.integratePickTimeModalRef.result.then(value => {
						if (value) {
							this.connectedToPicTime = true;
						}
					});
				},
				error => this.messageService.handleError(error)
			);
	}

	editPicTimeIntegrationSettings() {
		this.pickTimeDataProvider
			.getPreFillPicTimeSettings()
			.pipe(
				finalize(() => {
					this.isPicTimeSpinningWheelActive = false;
				}),
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				(data): void => {
					this.clientPortalText =
						data.data.clientPortalText !== null
							? data.data.clientPortalText
							: '';
					this.integratePickTimeModalRef = this.modalService.openModal(
						IntegratePicTimeModal,
						{
							windowClass: 'ng-bootstrap-modal-middle'
						}
					);
					this.integratePickTimeModalRef.componentInstance.clientPortalText = this.clientPortalText;
				},
				error => this.messageService.handleError(error)
			);
	}

	disconnectPicTime(): void {
		this.lockIntegrationButtons = true;
		this.isPicTimeDisconnectSpinningWheelActive = true;

		if (this.picTimeIntegration.actions.DISCONNECT_PIC_TIME) {
			this.pickTimeDataProvider
				.disconnectPickTimeIntegration(
					this.picTimeIntegration.actions.DISCONNECT_PIC_TIME.url
				)
				.pipe(
					finalize(() => {
						this.lockIntegrationButtons = false;
						this.isPicTimeDisconnectSpinningWheelActive = false;
					}),
					takeUntil(this.unsubscribe)
				)
				.subscribe(
					(): void => {
						this.successDisconnectPicTime();
					},
					error => this.messageService.handleError(error)
				);
		}
	}

	private openXeroAlertModal(title: string, message: string): void {
		const modal = this.modalService.openModal(ConfirmGeneralModalComponent);

		modal.componentInstance.title = title;
		modal.componentInstance.text = message;
		modal.componentInstance.buttons = [
			{
				text: 'Cancel',
				color: 'default',
				size: 'lg',
				action: 'DISCARD_ALERT'
			},
			{
				text: 'Continue to 2FA setup',
				color: 'success',
				size: 'lg',
				action: 'REDIRECT_TO_PROFILE'
			}
		];

		modal.result.then((action: string): void => {
			if (action === 'REDIRECT_TO_PROFILE') {
				this.location.go('/settings/profile/?redirectTo=2FA');
			}
		});
	}

	private openXeroAlertModalWithoutRedirection(
		title: string,
		message: string
	): void {
		const modal = this.modalService.openModal(ConfirmGeneralModalComponent);

		modal.componentInstance.title = title;
		modal.componentInstance.text = message;
		modal.componentInstance.buttons = [
			{
				text: 'Got it!',
				color: 'default',
				size: 'lg',
				action: 'REDIRECT_TO_PROFILE'
			}
		];
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./web/modern/components/settings/integrations-section/integrations-section.ts